import {useMutation} from '../../utils/react-query/hooks';
import type {UploadedFile} from './FileUploaderFileListItem';
import {useSendFileUploadRequest} from './utils';
import type {StripeFileFragmentData} from './stripeFileFragment';

export const UPLOAD_FILE_KEY = 'upload-file';

export const useUploadFile = (
  uploadedFile: UploadedFile,
  purpose: string,
  onFileSuccessfullyUploaded: (
    fileId: string,
    stripeFile: StripeFileFragmentData,
  ) => void,
  onError?: (error: Error) => void,
  consentedToShareWithPlatform?: boolean,
) => {
  const {localFile, stripeFile} = uploadedFile;
  const sendFileUploadRequest = useSendFileUploadRequest();

  return useMutation<StripeFileFragmentData | undefined, Error>(
    [UPLOAD_FILE_KEY, uploadedFile.id],
    async () => {
      if (stripeFile || !localFile) return undefined;

      return sendFileUploadRequest({
        file: localFile,
        purpose,
        consentedToShareWithPlatform,
      });
    },
    {
      onError: (error) => {
        if (onError) {
          onError(error);
        }
      },
      onSuccess: (stripeFile) => {
        if (stripeFile) onFileSuccessfullyUploaded(uploadedFile.id, stripeFile);
      },
      meta: {
        expectedErrors: [
          'upload_not_supported',
          'upload_image_format_not_supported',
          'upload_type_not_supported',
          'upload_ext_invalid',
          // Note: this expected error comes from reading the file from the client. It will be different per language, so this is our best effort to mark it as expected given locale distribution
          'The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired.',
          'upload_too_large',
        ],
      },
    },
  );
};
